/**
 * Coletor do devtools — o "o que aconteceu", sem nada de tela.
 *
 * Genérico por CANAL desde o início: `cache`, `socket`, e o que vier depois
 * (http, render, plugins). Cada canal vira uma aba na barra. Quem instrumenta
 * só chama `registrar` com o canal certo; a barra descobre os canais sozinha,
 * a partir do que chegou.
 *
 * Existe porque quase tudo que a gente otimizou é invisível na tela: o cache
 * de leitura deixa a tela idêntica venha da rede ou da memória, e um evento
 * de socket que não chega não produz erro nenhum — só uma tela que não
 * atualiza. Sem instrumentação, essas duas perguntas ("o cache pegou?", "o
 * evento chegou?") só se respondem depois, no New Relic ou no log do backend.
 *
 * ── Custo zero quando desligado ──────────────────────────────────────────
 *
 * `ligado` é lido uma vez na carga e reavaliado só quando alguém liga/desliga
 * de propósito. Com ele falso, `registrar()` retorna na primeira linha: sem
 * alocar objeto, sem montar string, sem crescer array. É o que permite deixar
 * as chamadas no caminho quente (cada leitura de cache, cada evento de
 * socket) sem pensar duas vezes.
 *
 * Separado do painel de propósito: aqui não há React nem DOM, então dá para
 * testar comportamento (contadores, corte do buffer, liga/desliga) sem montar
 * componente.
 */

const CHAVE_LS = "mk:devtools";
const LIMITE_POR_CANAL = 200; // buffer circular — a barra mostra os últimos

let ligado = false;
let eventos = [];
let seq = 0;
const ouvintes = new Set();

const leFlag = () => {
  try {
    return window.localStorage.getItem(CHAVE_LS) === "1";
  } catch {
    // localStorage pode lançar (modo privado, cookies bloqueados). O devtools
    // nunca pode ser o motivo de a tela não abrir.
    return false;
  }
};

ligado = leFlag();

const avisar = () => {
  ouvintes.forEach(fn => {
    try {
      fn();
    } catch {
      // Um ouvinte quebrado não pode derrubar quem está registrando evento.
    }
  });
};

/**
 * Registra um evento. A PRIMEIRA LINHA é a guarda: com o devtools desligado
 * isto custa uma comparação booleana.
 *
 * `detalhe` pode ser função: montar o payload também é trabalho, e não deve
 * acontecer quando ninguém está olhando.
 *
 * @param {string} canal  "cache" | "socket" | ...
 * @param {string} tipo   classificação dentro do canal (hit, miss, recebido...)
 * @param {string} chave  o que aconteceu com o quê (a chave de cache, o nome do evento)
 */
export const registrar = (canal, tipo, chave, detalhe) => {
  if (!ligado) return;

  const conteudo = typeof detalhe === "function" ? detalhe() : detalhe;
  const ultimo = eventos[eventos.length - 1];

  // Repetição CONSECUTIVA vira contador, como o console do navegador faz.
  // Não é só economia de tela: o `server:heartbeat` chega a cada 5s e, sem
  // isto, enchia o buffer sozinho — empurrando para fora os eventos que
  // importam. Guarda o payload MAIS RECENTE: é o que o inspetor mostra, e o
  // interessante num evento repetido é o último estado, não o primeiro.
  if (
    ultimo &&
    ultimo.canal === canal &&
    ultimo.tipo === tipo &&
    ultimo.chave === chave
  ) {
    ultimo.vezes += 1;
    ultimo.em = Date.now();
    ultimo.detalhe = conteudo;
    avisar();
    return;
  }

  eventos.push({
    id: ++seq,
    em: Date.now(),
    canal,
    tipo,
    chave,
    vezes: 1,
    detalhe: conteudo
  });
  // Corte por canal: um canal barulhento (socket) não pode expulsar o
  // histórico de outro (cache) — foi por isso que o buffer deixou de ser único.
  const doCanal = eventos.filter(e => e.canal === canal);
  if (doCanal.length > LIMITE_POR_CANAL) {
    const excedente = new Set(doCanal.slice(0, doCanal.length - LIMITE_POR_CANAL).map(e => e.id));
    eventos = eventos.filter(e => !excedente.has(e.id));
  }
  avisar();
};

/** Canais que já produziram evento, na ordem em que apareceram. */
export const canais = () => [...new Set(eventos.map(e => e.canal))];

/**
 * Recorte de um canal: contadores por tipo + eventos (mais recente primeiro).
 * Sem canal, devolve tudo.
 */
export const resumo = (canal) => {
  const doCanal = canal ? eventos.filter(e => e.canal === canal) : eventos;

  // Soma `vezes`, nao o numero de linhas: um evento agrupado 40x aconteceu 40
  // vezes, e e isso que o contador da aba precisa dizer.
  const contagem = {};
  let total = 0;
  doCanal.forEach(ev => {
    contagem[ev.tipo] = (contagem[ev.tipo] || 0) + ev.vezes;
    total += ev.vezes;
  });

  return {
    canal,
    total,
    contagem,
    eventos: [...doCanal].reverse()
  };
};

/**
 * Números específicos do cache: quantas idas à rede o cache evitou.
 *
 * `hit` e `compartilhada` são as duas formas de evitar rede — a segunda é uma
 * chamada idêntica que aproveitou outra já em voo (single-flight).
 */
export const resumoCache = () => {
  const { contagem, eventos: evs } = resumo("cache");
  const hit = contagem.hit || 0;
  const compartilhada = contagem.compartilhada || 0;
  const miss = contagem.miss || 0;

  const porChave = new Map();
  evs.forEach(ev => {
    if (ev.tipo === "invalidacao") return;
    const atual = porChave.get(ev.chave) || { chave: ev.chave, hit: 0, miss: 0, compartilhada: 0 };
    if (atual[ev.tipo] !== undefined) atual[ev.tipo] += ev.vezes;
    porChave.set(ev.chave, atual);
  });

  const evitadas = hit + compartilhada;
  const leituras = evitadas + miss;

  return {
    evitadas,
    miss,
    invalidacoes: contagem.invalidacao || 0,
    aproveitamento: leituras === 0 ? null : Math.round((evitadas / leituras) * 100),
    porChave: [...porChave.values()].sort(
      (a, b) => b.hit + b.compartilhada - (a.hit + a.compartilhada)
    )
  };
};

export const estaLigado = () => ligado;

/** Liga/desliga e persiste, para sobreviver ao F5. */
export const alternar = (valor) => {
  ligado = valor === undefined ? !ligado : !!valor;
  try {
    if (ligado) window.localStorage.setItem(CHAVE_LS, "1");
    else window.localStorage.removeItem(CHAVE_LS);
  } catch {
    // Sem persistência (modo privado): vale só para a sessão atual.
  }
  if (!ligado) eventos = []; // desligar também limpa: nada fica pendurado
  avisar();
  return ligado;
};

export const limpar = (canal) => {
  eventos = canal ? eventos.filter(e => e.canal !== canal) : [];
  if (!canal) seq = 0;
  avisar();
};

/** Assina mudanças (a barra usa para re-renderizar). Devolve o cancelador. */
export const assinar = (fn) => {
  ouvintes.add(fn);
  return () => ouvintes.delete(fn);
};

export const devtools = {
  registrar,
  canais,
  resumo,
  resumoCache,
  estaLigado,
  alternar,
  limpar,
  assinar,
  CHAVE_LS
};
